import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  useCurrentPSAP,
  useActiveCapabilities,
} from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { selectUser } from '@rsos/sinatra';
import {
  smartPhrases,
  getSmartKeyTextSyntax,
  type SmartPhrase,
} from '@rsos/utils/SmartPhrases';
import SmartPhraseChip from '../SmartPhraseChip/SmartPhraseChip';
import useSmartPhraseContext from '../useSmartPhraseContext/useSmartPhraseContext';
import styles from './SmartPhraseEditor.module.css';
import {
  syncDOMFromValue,
  buildChipPortals,
  domToRaw,
  setCursorTextOffset,
  getCursorTextOffset,
} from './utils';

type SmartPhraseOption = (typeof smartPhrases)[number];

const BLOCKED_CHARS = ['{', '}'];
const BLOCKED_CHARS_REGEX = new RegExp(`[${BLOCKED_CHARS.join('')}]`, 'g');

interface DropdownState {
  search: string;
  triggerIndex: number;
}

export interface ChipPortal {
  el: HTMLElement;
  phrase: SmartPhrase;
}

interface Props {
  /** HTML name attribute for the underlying textarea */
  name: string;
  /** Current text content, may include smart phrase tokens */
  value: string;
  /** Called when the textarea loses focus */
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Called with the updated value whenever the content changes */
  onChange: (value: string) => void;
  /** Forwarded as a data attribute to the textarea for test selection */
  'data-name'?: string;
  /** Character that opens the smart phrase picker (defaults to '/') */
  triggerChar?: string;
}

/**
 * Rich-text editor that renders smart phrase tokens as inline chips.
 * Type the trigger character (default `/`) at a word boundary to open
 * the phrase picker; selecting a phrase inserts the token syntax and
 * renders it as a chip. Designed to be used as a controlled input —
 * wire `value`/`onChange`/`onBlur` directly or via a Formik adapter.
 */
const SmartPhraseEditor: React.FC<Props> = ({
  name,
  value,
  onBlur,
  onChange,
  'data-name': dataName,
  triggerChar = '/',
}) => {
  const psap = useCurrentPSAP();
  const activeCapabilities = useActiveCapabilities();
  const user = useSelector(selectUser);

  const phrases = useMemo(
    () =>
      smartPhrases.filter(
        phrase =>
          !('capability' in phrase) ||
          Boolean(activeCapabilities[phrase.capability]),
      ),
    [activeCapabilities],
  );

  const context = useSmartPhraseContext({
    psap,
    user,
  });

  const editorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdown, setDropdown] = useState<DropdownState | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [chipPortals, setChipPortals] = useState<ChipPortal[]>([]);
  // Tracks the last value we wrote to the DOM to avoid re-syncing during user input
  const userValueRef = useRef(value);

  const syncEditor = useCallback((el: HTMLDivElement, newValue: string) => {
    syncDOMFromValue(el, newValue);
    setChipPortals(buildChipPortals(el));
  }, []);

  // useLayoutEffect so portals are filled before the browser paints (no empty chip flash)
  useLayoutEffect(() => {
    if (editorRef.current) {
      syncEditor(editorRef.current, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (editorRef.current && value !== userValueRef.current) {
      syncEditor(editorRef.current, value);
      userValueRef.current = value;
    }
  }, [value, syncEditor]);

  const filteredPhrases = useMemo(
    () =>
      dropdown
        ? phrases.filter(
            p =>
              p.display.toLowerCase().includes(dropdown.search.toLowerCase()) ||
              p.key.toLowerCase().includes(dropdown.search.toLowerCase()),
          )
        : [],
    [dropdown, phrases],
  );

  const closeWithCleanup = useCallback(
    (restoreFocus = false) => {
      if (!dropdown || !editorRef.current) {
        return;
      }

      const currentRaw = domToRaw(editorRef.current);

      //if the trigger char is already gone the dropdown was already cleaned up
      if (currentRaw[dropdown.triggerIndex] !== triggerChar) {
        setDropdown(null);
        return;
      }

      const endIndex = dropdown.triggerIndex + 1 + dropdown.search.length;
      const newValue =
        currentRaw.slice(0, dropdown.triggerIndex) + currentRaw.slice(endIndex);

      syncEditor(editorRef.current, newValue);
      userValueRef.current = newValue;
      onChange(newValue);
      setDropdown(null);

      if (restoreFocus) {
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.focus();
            setCursorTextOffset(editorRef.current, dropdown.triggerIndex);
          }
        }, 0);
      }
    },
    [dropdown, onChange, syncEditor, triggerChar],
  );

  const selectPhrase = useCallback(
    (phrase: SmartPhraseOption) => {
      if (!dropdown || !editorRef.current) {
        return;
      }

      const cursorPos = getCursorTextOffset(editorRef.current);
      const currentRaw = domToRaw(editorRef.current);
      const inserted = getSmartKeyTextSyntax(phrase.key);
      const newValue =
        currentRaw.slice(0, dropdown.triggerIndex) +
        inserted +
        currentRaw.slice(cursorPos);
      const newCursorPos = dropdown.triggerIndex + inserted.length;

      syncEditor(editorRef.current, newValue);
      userValueRef.current = newValue;
      onChange(newValue);
      setDropdown(null);
      setHighlightedIndex(0);

      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          setCursorTextOffset(editorRef.current, newCursorPos);
        }
      }, 0);
    },
    [dropdown, onChange, syncEditor],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (BLOCKED_CHARS.includes(e.key)) {
        e.preventDefault();
        return;
      }

      if (dropdown) {
        if (e.key === 'Escape') {
          closeWithCleanup(true);
          return;
        }

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex(prev =>
            filteredPhrases.length ? (prev + 1) % filteredPhrases.length : 0,
          );
          return;
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex(prev =>
            filteredPhrases.length
              ? (prev - 1 + filteredPhrases.length) % filteredPhrases.length
              : 0,
          );
          return;
        }

        if (e.key === 'Enter' && filteredPhrases.length > 0) {
          e.preventDefault();
          selectPhrase(filteredPhrases[highlightedIndex]);
          return;
        }
      }

      // if cursor is immediately after a chip, delete the whole chip
      if (e.key === 'Backspace' && editorRef.current) {
        const sel = window.getSelection();

        if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).collapsed) {
          const range = sel.getRangeAt(0);
          let chipToDelete: HTMLElement | null = null;

          if (
            range.startContainer === editorRef.current &&
            range.startOffset > 0
          ) {
            const prev = editorRef.current.childNodes[range.startOffset - 1];

            if (prev instanceof HTMLElement && prev.dataset.phraseKey) {
              chipToDelete = prev;
            }
          } else if (
            range.startContainer.nodeType === Node.TEXT_NODE &&
            range.startOffset === 0
          ) {
            const prev = range.startContainer.previousSibling;

            if (prev instanceof HTMLElement && prev.dataset.phraseKey) {
              chipToDelete = prev;
            }
          }

          if (chipToDelete) {
            e.preventDefault();

            const deletedEl = chipToDelete;

            chipToDelete.remove();

            const newRaw = domToRaw(editorRef.current);

            userValueRef.current = newRaw;
            onChange(newRaw);
            setChipPortals(prev => prev.filter(p => p.el !== deletedEl));
            return;
          }
        }
      }

      // insert \n via text node to prevent the browser inserting block elements
      if (e.key === 'Enter') {
        e.preventDefault();

        const sel = window.getSelection();

        if (sel && sel.rangeCount > 0 && editorRef.current) {
          const range = sel.getRangeAt(0);

          range.deleteContents();

          const textNode = document.createTextNode('\n');

          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);

          const newRaw = domToRaw(editorRef.current);

          userValueRef.current = newRaw;
          onChange(newRaw);
        }

        return;
      }

      // Trigger char: open phrase dropdown at word boundary
      if (e.key === triggerChar && editorRef.current) {
        const cursorPos = getCursorTextOffset(editorRef.current);
        const currentRaw = domToRaw(editorRef.current);
        const charBefore = cursorPos > 0 ? currentRaw[cursorPos - 1] : null;
        const isIsolated = charBefore === null || /\s/.test(charBefore);

        if (isIsolated) {
          setDropdown({ search: '', triggerIndex: cursorPos });
          setHighlightedIndex(0);
        }
      }
    },
    [
      closeWithCleanup,
      dropdown,
      filteredPhrases,
      highlightedIndex,
      selectPhrase,
      onChange,
      triggerChar,
    ],
  );

  const handleInput = useCallback(() => {
    if (!editorRef.current) {
      return;
    }

    const newRaw = domToRaw(editorRef.current);
    const cursorPos = getCursorTextOffset(editorRef.current);

    userValueRef.current = newRaw;
    onChange(newRaw);

    if (dropdown) {
      if (newRaw[dropdown.triggerIndex] !== triggerChar) {
        setDropdown(null);
        return;
      }

      const search = newRaw.slice(dropdown.triggerIndex + 1, cursorPos);

      setDropdown(prev => (prev ? { ...prev, search } : null));
      setHighlightedIndex(0);
    }
  }, [dropdown, onChange, triggerChar]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();

      let text = e.clipboardData.getData('text');

      text = text.replace(BLOCKED_CHARS_REGEX, '');

      if (!editorRef.current || !text) {
        return;
      }

      const sel = window.getSelection();

      if (!sel || sel.rangeCount === 0) {
        return;
      }

      const range = sel.getRangeAt(0);

      range.deleteContents();

      const textNode = document.createTextNode(text);

      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      const newRaw = domToRaw(editorRef.current);

      userValueRef.current = newRaw;
      onChange(newRaw);
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (dropdown) {
        closeWithCleanup(false);
      }

      // Synthesize target.name so Formik's handleBlur sets the touched state correctly
      const syntheticEvent = { ...e, target: { ...e.target, name } };

      onBlur(
        syntheticEvent as unknown as React.FocusEvent<HTMLTextAreaElement>,
      );
    },
    [closeWithCleanup, dropdown, name, onBlur],
  );

  // Close dropdown when cursor moves before the trigger index
  useEffect(() => {
    if (!dropdown) {
      return;
    }

    const handleSelectionChange = () => {
      if (!editorRef.current) {
        return;
      }

      const cursorPos = getCursorTextOffset(editorRef.current);

      if (cursorPos <= dropdown.triggerIndex) {
        setDropdown(null);
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange);
  }, [dropdown]);

  // when highlightedIndex changes, scroll the corresponding item into view if needed
  useEffect(() => {
    if (!dropdownRef.current) {
      return;
    }

    const item = dropdownRef.current.children[highlightedIndex];

    item?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex]);

  // Close dropdown on outside clicks
  useEffect(() => {
    if (!dropdown) {
      return;
    }

    const handleOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !editorRef.current?.contains(e.target as Node) &&
        editorRef.current !== e.target
      ) {
        closeWithCleanup(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);

    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdown, closeWithCleanup]);

  return (
    <div className={styles.wrapper}>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-name={dataName}
        className={styles.editor}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handlePaste}
      />
      {chipPortals.map(({ el, phrase }) =>
        createPortal(
          <SmartPhraseChip
            key={phrase.key}
            phrase={phrase}
            context={context}
            variant="admin"
          />,
          el,
        ),
      )}
      {dropdown && filteredPhrases.length > 0 && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {filteredPhrases.map((phrase, idx) => (
            <button
              key={phrase.key}
              type="button"
              className={`${styles.dropdownItem} ${idx === highlightedIndex ? styles.highlighted : ''}`}
              onMouseDown={e => {
                e.preventDefault();
                selectPhrase(phrase);
              }}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              <span className={styles.phraseDisplay}>{phrase.display}</span>
              <span className={styles.phraseDescription}>
                {phrase.description}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmartPhraseEditor;
